import { z } from "zod";

import { DIRECTORY_OWNED_KINDS, OWNED_PARTY_NOUNS, type DirectoryOwnedKind } from "./directory";
import { DOCUMENT_LINKER } from "./document-links";
import { ACCESS_AREAS } from "./organizations";
import { listOfFields } from "./parties";

// Work: what somebody in the organization has to do, one item at a time, kept in the work items table and listed on
// the Work page. There are two kinds. The Directory agent raises one for a party it added to the Directory without
// every field a person must fill in, and the item closes itself when somebody completes that party or removes it. The
// Document linker raises the other for a document it found no party of the Directory in, and that item closes itself
// when somebody links the document to one. Nobody closes an item by hand. The API and the frontend share these.

/** Who raised a work item that no person raised: the agent that files the parties a document names. */
export const DIRECTORY_AGENT = "Directory agent";

/** Where a work item stands: somebody still has to do it, or somebody has. */
export const WORK_ITEM_STATES = ["open", "done"] as const;
export type WorkItemState = (typeof WORK_ITEM_STATES)[number];

/**
 * How an item was closed: its party was completed, or it was taken out of the Directory instead — or its document was
 * linked to a party, by somebody or by the Document linker.
 */
export const WORK_ITEM_RESOLUTIONS = ["completed", "removed", "linked"] as const;
export type WorkItemResolution = (typeof WORK_ITEM_RESOLUTIONS)[number];

/** What kind of work an item is, which decides what its drawer offers. */
export const WORK_ITEM_KINDS = ["complete-party", "link-document"] as const;
export type WorkItemKind = (typeof WORK_ITEM_KINDS)[number];

/** A work item's id: letters, digits, `_` and `-`, so it fits a page link and a URL as it is. */
export const workItemIdSchema = z.string().regex(/^[\w-]{1,128}$/, "Invalid work item");

/** An Intake document a work item names: its id, and its file name when the item was raised. */
const workItemDocumentSchema = z.object({ fileId: z.string().min(1), fileName: z.string().min(1).max(255) });

/** What every work item has, whatever kind it is. */
const workItemBaseSchema = z.object({
	id: workItemIdSchema,
	/**
	 * The part of the business the work belongs to. A member sees the items of every area they have at least reader
	 * access to, and only an editor of it can do what an item asks.
	 */
	area: z.enum(ACCESS_AREAS),
	/** What to do, in a few words: "Complete Zelena Taxi", "Link invoice.pdf". */
	title: z.string().min(1).max(200),
	/** Why, and what exactly: where the party came from and which fields it lacks, or which document is unlinked. */
	detail: z.string().max(600),
	state: z.enum(WORK_ITEM_STATES),
	/** When it was raised, as an ISO 8601 timestamp in UTC. */
	createdAt: z.iso.datetime(),
	/** Who raised it: `DIRECTORY_AGENT` or `DOCUMENT_LINKER`, for every item there is today. */
	createdBy: z.string().min(1).max(80),
	/** When it was closed, as an ISO 8601 timestamp in UTC; absent while it is open. */
	closedAt: z.iso.datetime().optional(),
	/** The name of the member who closed it, as it was then, or `DOCUMENT_LINKER`; absent while it is open. */
	closedBy: z.string().min(1).max(80).optional(),
	resolution: z.enum(WORK_ITEM_RESOLUTIONS).optional(),
});

/** An item that asks somebody to fill in what a party the Directory agent added lacks. */
const completePartyWorkItemSchema = workItemBaseSchema.extend({
	kind: z.literal("complete-party"),
	/** The party to complete: its kind, its id in the table that owns it, and its name when the item was raised. */
	party: z.object({
		kind: z.enum(DIRECTORY_OWNED_KINDS),
		id: z.string().min(1).max(64),
		name: z.string().min(1).max(160),
	}),
	/** The labels of the fields the party lacked when the item was raised, in form order. */
	missing: z.array(z.string().min(1).max(40)).max(10),
	/** The Intake document the party was found in, when an agent found it in one. */
	source: workItemDocumentSchema.optional(),
});

/** An item that asks somebody to link a document the Document linker found no party of the Directory in. */
const linkDocumentWorkItemSchema = workItemBaseSchema.extend({
	kind: z.literal("link-document"),
	/** The document to link. */
	document: workItemDocumentSchema,
});

/** A work item of either kind, told apart by its `kind`. */
export const workItemSchema = z.discriminatedUnion("kind", [completePartyWorkItemSchema, linkDocumentWorkItemSchema]);
export type WorkItem = z.infer<typeof workItemSchema>;
export type CompletePartyWorkItem = Extract<WorkItem, { kind: "complete-party" }>;
export type LinkDocumentWorkItem = Extract<WorkItem, { kind: "link-document" }>;

/** How an open item is closed: when, by whom, and what became of it. */
export interface WorkItemClosing {
	readonly closedAt: string;
	readonly closedBy: string;
	readonly resolution: WorkItemResolution;
}

/**
 * The id of the item that asks somebody to complete a party. There is one per party, so raising it twice leaves one
 * item, and completing or removing the party finds it without looking.
 */
export function completePartyWorkItemId(kind: DirectoryOwnedKind, partyId: string): string {
	return `complete-${kind}-${partyId}`;
}

/** Everything the item for an incomplete party is made of that the party itself does not say. */
export interface CompletePartyWork {
	readonly party: CompletePartyWorkItem["party"];
	/** The labels of what the party lacks, in form order (`missingVendorFields` and the like); never empty. */
	readonly missing: readonly string[];
	readonly source?: CompletePartyWorkItem["source"];
	readonly createdAt: string;
}

/**
 * The open item that asks somebody to fill in what a party the Directory agent added lacks: "Complete Zelena Taxi" /
 * "The Directory agent added this vendor from receipt-taxi-ljubljana.jpg. Fill in Category, Payment terms and Start
 * date." The agent raises it, the local API and the frontend's fake API seed it, and all three build it here.
 */
export function completePartyWorkItem({ party, missing, source, createdAt }: CompletePartyWork): CompletePartyWorkItem {
	const from = source === undefined ? "" : ` from ${source.fileName}`;
	return {
		id: completePartyWorkItemId(party.kind, party.id),
		kind: "complete-party",
		area: "directory",
		title: `Complete ${party.name}`,
		detail: `The ${DIRECTORY_AGENT} added this ${OWNED_PARTY_NOUNS[party.kind]}${from}. Fill in ${listOfFields(missing)}.`,
		state: "open",
		createdAt,
		createdBy: DIRECTORY_AGENT,
		party,
		missing: [...missing],
		...(source !== undefined && { source }),
	};
}

/**
 * The id of the item that asks somebody to link a document. There is one per document, so raising it twice leaves one
 * item, and the first link made to the document finds it without looking.
 */
export function linkDocumentWorkItemId(fileId: string): string {
	return `link-document-${fileId}`;
}

/** Everything the item for a document with no party is made of. */
export interface LinkDocumentWork {
	readonly document: LinkDocumentWorkItem["document"];
	readonly createdAt: string;
}

/** How much of a document's name the item's title carries: "Link " takes the other 5 of its 200. */
const LINK_TITLE_FILE_NAME_CHARACTERS = 195;

/**
 * The open item that asks somebody to link a document the Document linker found no party of the Directory in: "Link
 * invoice.pdf" / "The Document linker found no party in the Directory that invoice.pdf names. Link it to the parties
 * it is about." It belongs to the Directory, whose editors are who may link a document. A file name may be 255
 * characters long, so a long one is shortened in the title, with a single-character ellipsis, and kept whole in the
 * detail.
 */
export function linkDocumentWorkItem({ document, createdAt }: LinkDocumentWork): LinkDocumentWorkItem {
	const name =
		document.fileName.length <= LINK_TITLE_FILE_NAME_CHARACTERS
			? document.fileName
			: `${document.fileName.slice(0, LINK_TITLE_FILE_NAME_CHARACTERS - 1).trimEnd()}…`;
	return {
		id: linkDocumentWorkItemId(document.fileId),
		kind: "link-document",
		area: "directory",
		title: `Link ${name}`,
		detail: `The ${DOCUMENT_LINKER} found no party in the Directory that ${document.fileName} names. Link it to the parties it is about.`,
		state: "open",
		createdAt,
		createdBy: DOCUMENT_LINKER,
		document,
	};
}

/**
 * Where an item comes in the list of its state, as one string: an open item by when it was raised and a done one by
 * when it was closed, then its id, so two at the same moment order the same way on every read. A page link carries
 * exactly this, and nothing else may order the list.
 */
export function workItemPosition(item: WorkItem): string {
	const at = item.state === "done" ? (item.closedAt ?? item.createdAt) : item.createdAt;
	return `${at}#${item.id}`;
}

/**
 * Whether the item at position `a` comes before the one at `b` in the list of `state`: open items oldest first, because
 * the one that has waited longest is the one to do next; done items newest first, because what was just finished is
 * what somebody looks back for.
 */
export function comesBefore(a: string, b: string, state: WorkItemState): boolean {
	return state === "open" ? a < b : a > b;
}

/** The items of `state` in the order the Work page lists them (`comesBefore`), each of the kind it was. */
export function inWorkOrder<Item extends WorkItem>(items: readonly Item[], state: WorkItemState): Item[] {
	return items
		.filter(item => item.state === state)
		.toSorted((a, b) => {
			const [first, second] = [workItemPosition(a), workItemPosition(b)];
			if (first === second) {
				return 0;
			}
			return comesBefore(first, second, state) ? -1 : 1;
		});
}

/** The Work page lists this many items a page. */
export const WORK_ITEMS_PAGE_SIZE = 10;

/** A link to another page of work items. The API makes it; a client passes it back unchanged and never reads it. */
export const workItemsPageTokenSchema = z.string().regex(/^[\w-]{1,512}$/, "Invalid page");

/** The query of `GET /api/work-items`: which state to list, the open items unless it says otherwise, and which page. */
export const workItemsQuerySchema = z.object({
	state: z.enum(WORK_ITEM_STATES).default("open"),
	page: workItemsPageTokenSchema.optional(),
});
export type WorkItemsQuery = z.infer<typeof workItemsQuerySchema>;

/** One page of the items a member may see in one state, with links to the pages beside it: `null` where there is none. */
export const workItemsPageSchema = z.object({
	items: z.array(workItemSchema).max(WORK_ITEMS_PAGE_SIZE),
	/** How many items of this state the member may see, every page counted: the rail's badge for the open ones. */
	total: z.int().nonnegative(),
	next: workItemsPageTokenSchema.nullable(),
	previous: workItemsPageTokenSchema.nullable(),
});
export type WorkItemsPage = z.infer<typeof workItemsPageSchema>;
